import { useEffect, useMemo, useState } from "react";
import "react-data-grid/lib/styles.css";
import DataGrid, {
  CellClickArgs,
  CellMouseEvent,
  Column,
  SortColumn,
} from "react-data-grid";
import "./dataGridStyles.scss";
import {
  AlphaNumbericType,
  FilterValueModel,
  IDataSetColumnsModel,
  ISearchParamsModel,
  ITableConfigModel,
  ObjectModel,
  TableTypeEnum,
} from "../../tsmodels/SharedModel";
import { getStringValue } from "../../helpers/GenericHelpers";
import {
  renderHeaderFilters,
  renderCell,
  renderEditCell,
  getComparator,
} from "./DataGridFunctions";
import { v4 as uuidv4 } from "uuid";
import { defaultGridConfig } from "../../config/DataGridConfig";

interface IReactDataGridProps {
  gridRows: ObjectModel<AlphaNumbericType>[];
  gridColumns: IDataSetColumnsModel[];
  gridInitalValues?: ObjectModel<FilterValueModel>;
  config?: ITableConfigModel;
  loadMoreRows?: (
    count: number,
    currentLength: number,
    sortColumns?: SortColumn[],
    filters?: ObjectModel<FilterValueModel>,
    nestedDatasetName?: string
  ) => Promise<any>;
  rowsRef?: any;
  meta?: any;
  tableType?: TableTypeEnum;
  className?: string;
  onCellClick?: (
    args: CellClickArgs<any, unknown>,
    event: CellMouseEvent
  ) => void;
  searchParams?: ISearchParamsModel;
}

const DataGridComponent = (props: IReactDataGridProps) => {
  // props
  const {
    gridRows,
    gridColumns,
    config = defaultGridConfig,
    loadMoreRows,
    rowsRef,
    meta,
    tableType = TableTypeEnum.MASTER,
    className,
    onCellClick,
    searchParams,
  } = props;

  // console.log('config: ', config)

  // console.log('gridColumns: ', gridColumns)

  // states
  const [rows, setRows] = useState<ObjectModel<AlphaNumbericType>[]>([]);

  const [columns, setColumns] = useState<Column<any, any>[]>([]);

  const [sortColumns, setSortColumns] = useState<SortColumn[]>([]);

  const [selectedRows, setSelectedRows] = useState(
    (): ReadonlySet<number> => new Set()
  );

  const [columnsOrder, setColumnsOrder] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [totalRows, setTotalRows] = useState(0);

  const [exportDropdown, setExportDropdown] = useState("");

  const [optionsDropdown, setOptionsDropdown] = useState("");

  // filter states
  const [filterPanel, setFilterPanel] = useState(true);
  const [filterOpen, setFilterOpen] = useState("");
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [filters, setFilters] = useState<ObjectModel<FilterValueModel>>({});
  // const [filtersValues, setFiltersValues] = useState<
  //   ObjectModel<IFiltersValuesModel>
  // >({});

  // funcs - toggle
  const toggleExportDropdown = (value: string) =>
    setExportDropdown((prev) => (prev === value ? "" : value));

  // const toggleOptionsDropdown = (value: string) =>
  //   setOptionsDropdown((prev) => (prev === value ? "" : value));

  // const toggleFilterPanel = () => setFilterPanel((prev) => !prev);

  // const toggleFilterOpen = (value: string) =>
  //   setFilterOpen((prev) => (prev === value ? "" : value));

  // funcs -helpers
  const handleCellClick = (
    args: CellClickArgs<any, unknown>,
    event: CellMouseEvent
  ) => {
    onCellClick && onCellClick(args, event);
  };

  // const handleFilters = (
  //   e: any,
  //   key: string,
  //   type: InputTypesEnum,
  //   column: any
  // ) => {
  //   const value = getTypeValue(e, type);
  //   setFilters((prev) => ({
  //     ...prev,
  //     [key]: {
  //       value,
  //       type: column?.columnType,
  //     },
  //   }));
  // };

  // const handleFilterType = (e: any, column: any) => {
  //   const value = e?.value
  //   const key = column?.key
  //   if (value) {
  //     setFiltersValues(prev => {
  //       const filterValue = {
  //         [key]: {
  //           ...prev?.[key],
  //           filterType: value,
  //           type: column?.info?.columnDatatype,
  //           name: column?.name,
  //           value: [],
  //         }
  //       }
  //       return ({ ...prev, ...filterValue })
  //     })
  //   } else {
  //     setFiltersValues(prev => {
  //       if (prev?.[key]) {
  //         delete prev?.[key]
  //       }
  //       return prev
  //     })
  //   }
  // }

  // console.log(filtersValues)

  // get cell class names
  const getCellClass = (row: any, key: string) => {
    let className = "cellClassName";

    if (row?.tableType === TableTypeEnum.DETAIL) {
      className = className + " nested-table-cell";
    }

    if (row?.expanded) {
      className = className + " expanded-cell";
    }

    if (row?.rowId && optionsDropdown && row?.rowId === optionsDropdown) {
      className = className + " options-selected";
    }

    return `${className} ${key}`;
  };

  // generate the columns
  const getColumns = (columns: IDataSetColumnsModel[]): Column<any, any>[] => {
    return columns?.map((each, index) => {
      const key = getStringValue(each?.columnName);
      return {
        id: uuidv4(),
        key,
        info: each,
        headerCellClass: `filterColumnClassName ${
          each?.columnDatatype
        }-column ${key} ${each?.disableFunctions ? "pointer-none" : ""}`,
        cellClass: (row: any) => getCellClass(row, key),
        summaryCellClass: (row: any) => getCellClass(row, key),
        columnType: each?.columnDatatype,
        name: each?.hideHeader
          ? ""
          : meta?.columnNames?.[key] ?? each?.aliasName ?? each?.columnName,
        frozen:
          !each?.disableFunctions &&
          config?.pin &&
          config?.pinnedColumns?.includes(key),
        resizable: !each?.disableFunctions && config?.resizable,
        colSpan(args) {
          return index === 0 &&
            args.type === "ROW" &&
            args.row.tableType === TableTypeEnum.DETAIL
            ? columns?.length || 1
            : 1;
        },
        renderHeaderCell: (p) => renderHeaderFilters(p, config),
        renderCell: ({ column, row, tabIndex, onRowChange }) =>
          renderCell(
            column,
            row,
            tabIndex,
            onRowChange,
            each,
            config,
            exportDropdown,
            toggleExportDropdown,
          ),
        renderEditCell: ({ row, column, onRowChange }: any) =>
          renderEditCell(row, column, onRowChange, each),
        // renderSummaryCell: (row) => renderSummaryCell(row, config),
        ...(each?.width ? { width: each?.width } : {}),
        // ...(key === ColumnDataTypeEnum.EXPANDED ||
        // key === ColumnDataTypeEnum.EXPORT ||
        // key === ColumnDataTypeEnum.OPTIONS
        //   ? expandedColumnFields
        //   : {}),
      };
    });
  };

  // when columns are reordered change the index and other
  const onColumnsReorder = (sourceKey: string, targetKey: string) => {
    config?.reorder &&
      setColumnsOrder((prev) => {
        const source = prev.findIndex((each) => each === sourceKey);
        const target = prev.findIndex((each) => each === targetKey);
        const newOrder = Array.from(prev);
        const [removed] = newOrder.splice(source, 1);
        newOrder.splice(target, 0, removed);
        return newOrder;
      });
  };

  function isAtBottom({
    currentTarget,
  }: React.UIEvent<HTMLDivElement>): boolean {
    return (
      currentTarget.scrollTop + 10 >=
      currentTarget.scrollHeight - currentTarget.clientHeight
    );
  }

  const getInitalRows = async (
    count: number,
    currentLength: number,
    sortColumns?: SortColumn[],
    filters?: ObjectModel<FilterValueModel>,
    donAppend = false
  ) => {
    setIsLoading(true);

    // await
    const newRows = await loadMoreRows?.(
      count,
      currentLength,
      sortColumns,
      filters
    );

    const newRowsRawData = newRows?.data || [];
    const newRowsData = newRowsRawData?.map((each: any) => ({
      ...each,
      tableType,
      rowId: uuidv4(),
    }));

    setTotalRows((prev) => newRows?.totalRecords ?? prev);
    setRows(donAppend ? newRowsData : [...rows, ...newRowsData]);
    setIsLoading(false);
  };

  async function handleScroll(event: React.UIEvent<HTMLDivElement>) {
    if (
      isLoading ||
      !isAtBottom(event) ||
      rows?.length >= totalRows ||
      !config?.pagination
    )
      return;

    await getInitalRows(
      config?.pageCount,
      rows?.length || 0,
      sortColumns,
      filters
    );
  }

  const onSortColumnsChange = (sortColumns: SortColumn[]) => {
    if (config?.pagination) {
      getInitalRows(config?.pageCount, 0, sortColumns, filters, true);
    }
    setSortColumns(sortColumns);
  };

  const onRowsChange = (rows: any, { indexes }: any) => {
    if (config?.enableNestedTable) {
      const row = rows[indexes[0]];
      // console.log("row: ", row);
      if (row.tableType === TableTypeEnum.MASTER) {
        if (row.expanded) {
          rows.splice(indexes[0] + 1, 0, {
            tableType: TableTypeEnum.DETAIL,
            id: uuidv4(),
            parentId: row.id,
          });
        } else {
          rows.splice(indexes[0] + 1, 1);
        }
        setRows(rows);
      }
    } else {
      setRows(rows);
    }
  };

  // constants
  const filteredRows = useMemo(
    () =>
      // config?.filters && !config?.pagination && rows?.length > 0
      //   ? applyFilters(rows, filters as any, true)
      //   : 
     rows,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      JSON.stringify(rows),
      JSON.stringify(filters),
      config?.filters,
      config?.pagination,
    ]
  );

  const sortedRows = useMemo((): any[] => {
    const filteredRowsData = filteredRows;
    if (config?.pagination) return filteredRowsData;
    else if (sortColumns.length === 0) return filteredRowsData;
    else {
      return [...filteredRowsData].sort((a: any, b: any) => {
        for (const sort of sortColumns) {
          const comparator = getComparator(sort.columnKey, gridColumns);
          const compResult = comparator(a, b);
          if (compResult !== 0) {
            return sort.direction === "ASC" ? compResult : -compResult;
          }
        }
        return 0;
      });
    }
  }, [filteredRows, config?.pagination, sortColumns, gridColumns]);

  const reorderedColumns = useMemo(() => {
    return columnsOrder.map((index) =>
      columns?.find((each) => each?.key === index)
    );
  }, [columnsOrder, columns]);

  // funcs -render
  // const renderFilterAccordions = (column: any) => {
  //   const value = filtersValues?.[column?.name];
  //   return (
  //     <Accordion key={column?.key} open={filterOpen} toggle={toggleFilterOpen}>
  //       <AccordionItem>
  //         <AccordionHeader targetId={column?.key}>
  //           {column?.name}
  //         </AccordionHeader>
  //         <AccordionBody accordionId={column?.key}>
  //           <DropdownInput
  //             className="filterTypeMap"
  //             label="Filter Type"
  //             value={value}
  //             options={filterTypeMap()}
  //             onChange={(e: any) => handleFilterType(e, column)}
  //           />
  //         </AccordionBody>
  //       </AccordionItem>
  //     </Accordion>
  //   );
  // };

  // const renderFilterInputs = (filterColumns: any) => {
  //   const modifiedColumns =
  //     filterColumns?.filter((each: any) => !each?.info?.disableFunctions) || [];
  //   return modifiedColumns?.map((each: any) => renderFilterAccordions(each));
  // };

  // useEffects
  useEffect(() => {
    if (config?.pagination) {
      getInitalRows(config?.pageCount, rows?.length || 0, sortColumns, filters);
    } else {
      const searchRows =
        searchParams?.fieldName && searchParams?.value
          ? gridRows?.filter((each) =>
              `${each?.[searchParams?.fieldName]}`
                ?.toLowerCase()
                ?.trimStart()
                ?.trimEnd()
                ?.includes(
                  searchParams?.value?.toLowerCase()?.trimStart()?.trimEnd()
                )
            )
          : gridRows;
      setRows(searchRows);
    }
  }, [
    JSON.stringify(gridRows),
    JSON.stringify(config?.pagination),
    JSON.stringify(searchParams),
  ]);

  useEffect(() => {
    setColumns(getColumns(gridColumns));
    setColumnsOrder(
      gridColumns?.map((each) => getStringValue(each?.columnName))
    );
    // // Todo: handle rows change
    // setFilters(getInitalValues(gridColumns, rows || []));
  }, [
    JSON.stringify(gridColumns),
    JSON.stringify(config),
    JSON.stringify(meta),
    exportDropdown,
    optionsDropdown,
  ]);

  useEffect(() => {
    const WHITE_LIST = [
      "ResizeObserver loop completed with undelivered notifications.",
    ];
    window.addEventListener("error", (e) => {
      if (WHITE_LIST.includes(e.message)) {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div"
        );
        const resizeObserverErrs = document.querySelectorAll(
          "#webpack-dev-server-client-overlay"
        );
        if (resizeObserverErrs?.length > 0) {
          resizeObserverErrs.forEach((each) =>
            each?.setAttribute("style", "display: none")
          );
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute("style", "display: none");
        }
      }
    });

    return () => {
      window.removeEventListener("error", () => {});
    };
  }, []);

  useEffect(() => {
    if (rowsRef) {
      rowsRef.current = rows;
    }
  }, [rows, rowsRef]);

  return (
    <div className="react-grid-main rootClassname">
      {/* {config?.filters ? (
        <div className="filters-container-main">
          <Button onClick={toggleFilterPanel}>
            <FontAwesomeIcon icon={faFilter} />
          </Button>
          

          {filterPanel ? (
            <div className="filter-panel-container">
              <div className="filter-panel-header">
                <div className="filter-panel-title">Table Filters</div>
                <div className="filter-panel-close" onClick={toggleFilterPanel}>
                  <FontAwesomeIcon icon={faXmark} />
                </div>
              </div>

              <div className="filter-panel-inputs">
                {renderFilterInputs(columns)}
              </div>

              <div className="filter-panel-footer">
                <Button>Apply</Button>
                <Button>Clear All</Button>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )} */}

      <DataGrid
        className={`fill-grid rdg-light
            ${config.filters ? "filterContainerClassname" : ""} ${className}`}
        columns={reorderedColumns as any}
        rows={sortedRows}
        onRowsChange={onRowsChange}
        defaultColumnOptions={{
          sortable: config.sortable,
          // resizable: config.resizable,
          draggable: config.draggable,
        }}
        onColumnsReorder={onColumnsReorder}
        selectedRows={selectedRows}
        onSelectedRowsChange={setSelectedRows}
        // onRowsChange={setRows}
        sortColumns={sortColumns}
        onSortColumnsChange={onSortColumnsChange}
        direction={"ltr"}
        rowHeight={60}
        headerRowHeight={50}
        onScroll={handleScroll}
        onCellClick={handleCellClick}
      />
      {isLoading && config?.pagination && (
        <div className="loadMoreRowsClassname">Loading more rows...</div>
      )}
    </div>
  );
};

export default DataGridComponent;
